import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import _ from 'lodash';
import Has from '../../component/Permission/Has';
import './Users.css'
import UserInformation from '../../component/User/UserInformation';
import UserRoleInformation from '../../component/User/UserRoleInformation';
import ToggleSwitch from '../../component/ToggleSwitch/ToggleSwitch';
import LoadingSpinner from '../../component/LoadingSpinner/LoadingSpinner';
import { FF_USERS_PAGE } from '../../config/FeatureFlagTypes';
import { U_USER_ADMIN } from '../../config/PermissionTypes';
import { activateCompanyUser, getUser, saveUser} from '../../service/UserService';
import { getCompanyRoles } from '../../service/CompanyService';
import { getUrl } from '../../util/EfupUtility';
import { Alert } from '../../component/Alert/Alert';
import { useCompanyPermission } from '../../component/CompanySelection/CompanyContext';
import ActivateUserModal from '../../component/User/ActivateUserModal';

export default function EditUser(props) {
    const [user, setUser] = useState({
        uuid: "",
        username: "",
        name: "",
        customerId: "",
        email: "",
        isActive: true,
        companyRole: {
            roleUuid: ""
        }
    });
    const [availableRoles, setAvailableRoles] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [userInfoUpdated, setUserInfoUpdated] = useState(false);
    const [savePending, setSavePending] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [banner, setBanner] = useState({
        show: false,
        type: "success",
        message: ""
    });
    const location = useLocation();
    const { companyNumber, userUuid, username } = location.state;

    useEffect(() => {
        const dataHandler = (userData) => {
            if (userData) {
                setUser(userData);
            } else {
                showBanner("error", `An error occurred while retrieving the user: ${username}`)
            }
        };
        getUser(userUuid, companyNumber, dataHandler);
        
        const roleDataHandler = (companyRoles) => {
            if (companyRoles) {
                setAvailableRoles(companyRoles);
            } else {
                showBanner("error", `An error occurred while retrieving company roles`)
            }
            setIsLoading(false);
        };
        getCompanyRoles(companyNumber, roleDataHandler);
        
    }, [userUuid, username, companyNumber])

    //if permissions are not loaded, show loading spinner
    const companyPermission = useCompanyPermission();
    if (!companyPermission) {
        return (
            <div id="dashboard-loader">
                <LoadingSpinner blue />
            </div>
        );
    }

    const isSaveDisabled = () => {
        const errorsExist = !_.isEmpty(errors);
        return !user.name || !user.username || !user.customerId || !user.companyRole.roleUuid || errorsExist || !userInfoUpdated || savePending;
    }

    const handleUserUpdate = (field, value) => {
        let updatedUser = {...user};
        updatedUser[field] = value
        setUser(updatedUser);
        setUserInfoUpdated(true);
    }

    const handleSaveUser = async () => {
        setSavePending(true);
        await Promise.resolve(saveUser(user, companyNumber))
            .then(() => { 
                setSavePending(false); 
                setUserInfoUpdated(false); 
                showBanner("success", <>Your changes were sucessfully updated! <Link to={getUrl(companyNumber, `/users/`)}>Back to Users page</Link></>)
            })
            .catch(error => {
                let message = error.response?.data || "An error occurred while trying to save your changes.";
                showBanner("error", message);
                setSavePending(false);
            });
    }

    const showBanner = (type, message) => {
        setBanner({
            show: true,
            type: type,
            message: message
        })
    }    

    const handleInactivateUser = () => {
        handleUserUpdate("isActive", false)
        setIsModalOpen(false);
    }

    const handleActivateUser = () => {
        activateCompanyUser(companyNumber, user.uuid, "Activate", !user.isActive, () => {
            setIsLoading(false);
            let updatedUser = {...user};
            updatedUser.isActive = true;
            setUser(updatedUser);
            showBanner("success", <>User was sucessfully activated! <Link to={getUrl(companyNumber, `/users/`)}>Back to Users page</Link></>);
        },
        (message) => {
            showBanner("error", message);
        })
    }

    return (
        <><Has isPageLevel={true} featureFlag={FF_USERS_PAGE} permission={U_USER_ADMIN}>
            {/* <!-- Page content--> */}
            <div className="container-fluid edit-users-page" data-testid="edit-users-page">
                <div className="cb-row page-title-area">
                    <div className="cb-col-xs-8 cb-col-lg-8 page-title">
                        <h1 data-testid="edit-user-page-header-title">Edit User</h1>
                    </div>
                </div>
                {isLoading ?
                <LoadingSpinner blue testId="edit-users-loading-spinner" />
                : 
                <><ActivateUserModal
                        isActivateModalOpen={isModalOpen}
                        setIsActivateModalOpen={setIsModalOpen}
                        activateChangeTo={!user.isActive}
                        user={user}
                        updateUser={handleInactivateUser}
                        companyNumber={companyNumber}
                        editPageUpdate={handleSaveUser}
                />
                {banner.show && 
                    <Alert type={`${banner.type}`} handleOnClose={() =>  setBanner({ show: false, message: ""})} dataTestId='edit-user-notification'>
                        <div style={{ fontWeight: '500' }}>{banner.message}</div>
                    </Alert>}
                <div className="user-info-container" data-testid="user-info-fields">
                    <div className="d-flex justify-content-between">
                        <div className="sectionTitle">User Information</div>
                        <ToggleSwitch 
                            id="inactivate-user"
                            label={user.isActive ? "Active User" : "Inactive User"}
                            dataTestId="inactivate-user"
                            checked={user.isActive}
                            onChange={checked => {
                                if (!checked) {
                                    setIsModalOpen(true);
                                } else {
                                    handleActivateUser();
                                }
                            }}
                            small
                        />
                    </div>
                    <UserInformation 
                        name={user.name}
                        userId={user.username}
                        email={user.email}
                        customerId={user.customerId}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    />
                </div>

                <div className="role-container" data-testid="user-role-options">
                    <UserRoleInformation 
                        existingRoleUuid={user.companyRole.roleUuid}
                        availableRoles={availableRoles}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    />
                </div>
                <div className="user-buttons" data-testid="edit-user-buttons">
                    <Link to={getUrl(companyNumber, `/users/`)}>
                        <button
                            type="button"
                            className="cb-btn cb-btn-outline-secondary"
                            data-testid="edit-user-cancel"
                            disabled={false}
                            onClick={() => null}>
                            Cancel
                        </button>
                    </Link>
                    <button 
                        className="cb-btn cb-btn-primary" 
                        data-testid="edit-user-submit" 
                        disabled={isSaveDisabled()}
                        onClick={() => handleSaveUser()}>
                        Update User
                    </button>
                </div></>}
            </div>
        </Has></>
    );
}